import * as React from "react";
import { Button, IconButton, InputAdornment} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import {
  ButtonsContainer,
  ErrorText,
  FieldContainer,
  FormContainer,
} from "./CreateUserPopup.styled";
import InputField from "../../../components/formFields/InputField";
import { ModalWindow } from "../../../shared/components/modalWindow/ModalWindow";
import { useGetNaicMemberQuery } from "../../../services/InsuranceService";
import useFetchWithMsal from "../../../utils/useFetchWithMsal";
import SelectField from "../../../shared/components/SelectField";
import SearchableSelect from "../../../components/formFields/SearchableSelect";
import {
  useCreateUserMutation,
  useGetAssociationsQuery,
  useGetRolesQuery,
} from "../../../services/UserService";
import { generatePassword } from "../../../shared/utils/generatePassword";
import { useEffect } from "react";
import { LoadingPanel } from "../../../shared/components/LoadingPanel";

export const CreateUserPopup = ({ open, onClose }) => {
  const { skip } = useFetchWithMsal();
  const { data: naicMembers } = useGetNaicMemberQuery("", {});
  const { data: roles } = useGetRolesQuery("", {
    skip,
  });
  const { data: associations } = useGetAssociationsQuery("", {
    skip,
  });
  const [createUser, { isSuccess, error, isLoading }] = useCreateUserMutation();

  useEffect(() => {
    if (isSuccess) {
      onClose();
    }
  }, [isSuccess]);

  const validationSchema = Yup.object().shape({
    userName: Yup.string().required("Username is required"),
    email: Yup.string()
      .required("Email is required")
      .matches(/^[\w-\.]{1,30}@([\w-]+\.)+[\w-]{2,4}$/, "Enter Valid Email"),
    password: Yup.string()
      .min(8, "Password must be at least 8 characters long")
      .max(64, "Password cannot be more than 64 characters long")
      .test(
        "password-strength",
        "Password must have at least 3 of the following: a lowercase letter, an uppercase letter, a digit, or a symbol",
        (value) => {
          if (!value) return false;
          const checks = [
            /[a-z]/.test(value),
            /[A-Z]/.test(value),
            /\d/.test(value),
            /[\W_]/.test(value),
          ];
          return checks.filter(Boolean).length >= 3;
        }
      ),
    roleId: Yup.string().required("Role is required"),
  });
  const handleSubmit = (values) => {
    const formData = { ...values, naicCode: values.naicCode?.naicCode };
    console.log("formData", formData);
    createUser(formData);
  };

  const copyToClipboard = (value) => {
    if (navigator?.clipboard) {
      navigator?.clipboard.writeText(value);
    }
  };

  const getInputComponent = (item, setFieldValue) => {
    switch (item?.type) {
      case "searchableSelect":
        const onChange = (value) => {
          if (value) {
            setFieldValue(
              "password",
              value.password ? value.password : "ILHIGA-"
            );
          }
        };
        return (
          <FieldContainer>
            <SearchableSelect
              name={item.name}
              label={item.label}
              fullWidth
              multiline
              options={naicMembers?.slice(1)}
              onChange={onChange}
              isHidden
            />
          </FieldContainer>
        );
      case "select":
        return (
          <FieldContainer>
            <SelectField
              name={item.name}
              label={item.label}
              data={item.data}
              fullWidth
            />
          </FieldContainer>
        );
      case "password":
        return (
          <FieldContainer>
            <InputField
              name={item.name}
              label={item.label}
              fullWidth
              isDisabled={item.isDisabled}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => copyToClipboard(item.value)}>
                      <ContentCopyIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </FieldContainer>
        );
      default:
        return (
          <FieldContainer>
            <InputField name={item.name} label={item.label} fullWidth />
          </FieldContainer>
        );
    }
  };
  return (
    <ModalWindow open={open} onClose={onClose} header="Create User">
      {isLoading && <LoadingPanel />}
      <Formik
        initialValues={{
          loginName: "",
          email: "",
          roleId: "",
          naicCode: "",
          associationGroup: 1,
          password: generatePassword(),
        }}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        {({ values, setFieldValue }) => (
          <Form id={"memberForm"}>
            <FormContainer>
              <ErrorText type="error">{error?.data?.Message}</ErrorText>

              {getInputComponent({
                name: "email",
                label: "Email:",
                type: "text",
              })}
              {getInputComponent({
                name: "userName",
                label: "Username:",
                type: "text",
              })}
              {getInputComponent({
                name: "roleId",
                label: "Role:",
                type: "select",
                data: roles,
              })}
              {(values.roleId === 1 || values.roleId === 2) &&
                getInputComponent({
                  name: "associationGroup",
                  label: "Association Group:",
                  type: "select",
                  data: associations,
                })}
              {values.roleId === 3 &&
                getInputComponent(
                  {
                    name: "naicCode",
                    label: "NAIC Code:",
                    type: "searchableSelect",
                  },
                  setFieldValue
                )}

              {getInputComponent({
                name: "password",
                label: "Password:",
                type: "password",
                value: values.password,
                isDisabled: values.roleId === 3,
              })}
            </FormContainer>
            <ButtonsContainer>
              <Button variant="outlined" type="submit">
                Save
              </Button>
              <Button variant="outlined" type="reset">
                Cancel
              </Button>
            </ButtonsContainer>
          </Form>
        )}
      </Formik>
    </ModalWindow>
  );
};
